
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

import Autocomplete, { AutocompleteOption } from '@/components/controls/autocomplete.vue';
import Taxonomy from '@/store/models/taxonomy';
import { BeforeAfterImagesActions, BeforeAfterImagesStatus } from '@/store/modules/before-after-images/types';
import BeforeAfterImage from '@/store/modules/before-after-images/before-after-image';
import ProductRelationship from '@/store/modules/products/product-relationship';

import NotificationBanner from '@/components/blocks/notification-banner.vue';
import FormInput from '@/components/controls/form-input.vue';
import ImageUploadControl from '@/components/controls/image-upload.vue';
import Publish from '@/components/forms/publish.vue';
import PracticeSelectionControl from '@/components/controls/practice-selection.vue';
import ProductSelection from '@/components/controls/product-selection.vue';
import Checkbox from '@/components/controls/checkbox.vue';
import BeforeAfterLicenseAgreementModal from '@/components/layout/before-after-license-agreement-modal.vue';

export default Vue.extend({
  name: 'EditBeforeAfterImageView',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    NotificationBanner,
    Publish,
    Autocomplete,
    ImageUploadControl,
    PracticeSelectionControl,
    ProductSelection,
    Checkbox,
    BeforeAfterLicenseAgreementModal,
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      treatmentsSearchInput: '',
      productAccessoriesInput: '',
      showLicenseModal: false,
    };
  },
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin', 'products', 'accessories']),
    /**
     * Before After Images
     */
    ...mapState('before-after-images', ['editable', 'editableStatus']),
    ...mapGetters('before-after-images', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
    }),

    /**
     * Products
     */
    ...mapState('products', {
      productsTree: 'tree',
    }),
    /** For non-admin: owned product ids from user profile (owned flag only); used to filter product dropdown. Admin gets undefined (no filter). */
    ownedProductIds(): number[] | undefined {
      if (this.isSuperAdmin || this.isAdmin) {
        return undefined;
      }
      const profileProducts = this.products as { id: number; owned?: boolean }[] | undefined;
      const owned = profileProducts?.filter((p) => p.owned !== false) ?? [];
      return owned.length ? owned.map((p) => p.id) : undefined;
    },

    /**
     * Component
     */
    isFetching() {
      return this.isLoading || this.isSaving;
    },
    isInactive() {
      return this.editable.status === BeforeAfterImagesStatus.Inactive;
    },
    treatments(): Taxonomy[] {
      if (this.editable && this.editable.product && this.editable.product.id && this.productsTree.length) {
        const product = this.productsTree.find((item) => item.id === this.editable.product.id);

        if (product) {
          return product.treatments;
        }
      }

      return [];
    },
    treatmentsOptions(): AutocompleteOption[] {
      const ids = this.editable.treatments.map(({ id }) => id);

      return this.treatments
        .filter(({ id }) => ids.indexOf(id) === -1)
        .map(({ id, title }) => ({
          label: title,
          value: id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.treatmentsSearchInput.toLowerCase()) > -1);
    },
    /** For non-admin: accessory ids from user profile (practice-owned); undefined for admin (no filter). */
    ownedAccessoryIds(): number[] | undefined {
      if (this.isSuperAdmin || this.isAdmin) {
        return undefined;
      }
      const profileAccessories = this.accessories as { id: number }[] | undefined;
      return profileAccessories?.length ? profileAccessories.map((a) => a.id) : undefined;
    },
    /** Product accessories for the selected product; for non-admin filtered to practice-owned only. */
    allProductAccessories(): Taxonomy[] {
      if (!this.editable || !this.editable.product || !this.editable.product.id || !this.productsTree.length) {
        return [];
      }
      const product = this.productsTree.find((item) => item.id === this.editable.product!.id);
      const accessories = product && product.accessories ? product.accessories : [];
      if (!this.ownedAccessoryIds || !this.ownedAccessoryIds.length) {
        return accessories;
      }
      const ownedSet = new Set(this.ownedAccessoryIds);
      return accessories.filter((a) => ownedSet.has(a.id));
    },
    getProductAccessories(): Taxonomy[] {
      const ids = this.selectedProductAccessories.map(({ id }) => id);
      return this.allProductAccessories.filter(({ id }) => ids.indexOf(id) === -1);
    },
    getProductAccessoriesOptions(): AutocompleteOption[] {
      return this.getProductAccessories
        .map((accessory) => ({
          label: accessory.title,
          value: accessory.id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.productAccessoriesInput.toLowerCase()) > -1);
    },
    hasProductAccessories(): boolean {
      return Boolean(this.allProductAccessories.length);
    },
    hasProductAccessoriesOptions(): boolean {
      return Boolean(this.getProductAccessories.length);
    },
    selectedProductAccessories(): Taxonomy[] {
      const availableIds = this.allProductAccessories.map(({ id }) => id);
      return (this.editable.accessories || []).filter(({ id }) => availableIds.indexOf(id) !== -1);
    },
  },
  methods: {
    updateNumberOfTreatments(value: string) {
      const num = value === '' ? null : parseInt(value, 10);
      this.update({
        numberOfTreatmentsPerformed: Number.isNaN(num) ? null : num,
      });
    },
    ...mapActions('before-after-images', {
      update: BeforeAfterImagesActions.UPDATE_EDITABLE,
      save: BeforeAfterImagesActions.SAVE_EDITABLE,
      removeAction: BeforeAfterImagesActions.DELETE,
      restoreAction: BeforeAfterImagesActions.RESTORE,
    }),
    selectProduct(productId?: number) {
      let product;

      if (productId) {
        product = this.productsTree.find((item) => item.id === productId);
      }

      if (!product) {
        this.update({ product: null, treatments: [], accessories: [] });
        return;
      }

      if (this.editable.product && product.id === this.editable.product.id) {
        return;
      }

      this.update({
        product: ProductRelationship.clone(product),
        treatments: [],
        accessories: [],
      });
    },
    addProductAccessory(option: AutocompleteOption) {
      const accessories = [...(this.editable.accessories || [])];
      const accessory = this.allProductAccessories.find((item) => item.id === option.value);
      if (!accessory) {
        return;
      }
      accessories.push(Taxonomy.clone(accessory));
      this.update({ accessories });
    },
    removeProductAccessory(accessoryId: number) {
      const accessories = (this.editable.accessories || []).filter(({ id }) => id !== accessoryId);
      this.update({ accessories });
    },
    addTreatment(option: AutocompleteOption) {
      const treatment = this.treatments.find((item) => item.id === option.value);
      if (!treatment) {
        return;
      }

      const treatments = [...this.editable.treatments];

      treatments.push(Taxonomy.clone(treatment));

      this.update({ treatments });
    },
    removeTreatment(treatmentId: number) {
      const treatments = this.editable.treatments.filter(({ id }) => id !== treatmentId);

      this.update({ treatments });
    },
    remove() {
      this.removeAction({ id: this.editable.id });
    },
    restore() {
      this.restoreAction({ id: this.editable.id });
    },
    updateImageData(data: Partial<BeforeAfterImage>) {
      const image = BeforeAfterImage.clone(this.editable.images[0]);

      Object.keys(data).forEach((key) => {
        image[key] = data[key];
      });

      this.update({ images: [image] });
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));

      if (!answer) {
        next(false);
        return;
      }
    }

    next();
  },
});
